import { createContext, MouseEvent, useMemo, useState } from 'react';

import { ApiPersonDetailReviewsListServiceTypeCategory as ServiceTypeCategory } from '@rover/rsdk/src/apiClient/latest/model/apiPersonDetailReviewsListServiceTypeCategory';
import { Logger } from '@rover/rsdk/src/modules/Logging';

type ReviewsModalContextValue = {
  selectedReviewIndex: number;
  isModalForAutomaticRecord: boolean;
  closeReviewsModal: () => void;
  toggleAutomaticRecordModal: () => void;
  openReviewsModal: (event?: MouseEvent<HTMLElement>, index?: number) => void;
  serviceTypeCategory: ServiceTypeCategory;
  setServiceTypeCategory: (category: ServiceTypeCategory) => void;
};

/** Context used for rendering the reviews modal on the trainer profile page */
const ReviewsModalContext = createContext<ReviewsModalContextValue>({
  selectedReviewIndex: -1,
  isModalForAutomaticRecord: false,
  closeReviewsModal: () =>
    Logger.info('Cannot call `closeReviewsModal`: ReviewsModalContextProvider not found'),
  toggleAutomaticRecordModal: () =>
    Logger.info('Cannot call `toggleAutomaticRecordModal`: ReviewsModalContextProvider not found'),
  openReviewsModal: () =>
    Logger.info('Cannot call `openReviewsModal`: ReviewsModalContextProvider not found'),
  serviceTypeCategory: 'dog-training',
  setServiceTypeCategory: () =>
    Logger.info('Cannot call `setServiceTypeCategory`: ReviewsModalContextProvider not found'),
});

export function ReviewsModalContextProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [selectedReviewIndex, setSelectedReviewIndex] = useState<number>(-1);
  const [isModalForAutomaticRecord, setIsModalForAutomaticRecord] = useState<boolean>(false);
  const [serviceTypeCategory, setServiceTypeCategory] =
    useState<ServiceTypeCategory>('dog-training');

  const contextValue = useMemo<ReviewsModalContextValue>(
    () => ({
      selectedReviewIndex,
      isModalForAutomaticRecord,
      closeReviewsModal: () => {
        if (isModalForAutomaticRecord) {
          setIsModalForAutomaticRecord(false);
          return;
        }
        setSelectedReviewIndex(-1);
      },
      toggleAutomaticRecordModal: () => {
        setIsModalForAutomaticRecord(!isModalForAutomaticRecord);
      },
      openReviewsModal: (event, index = 0) => {
        setSelectedReviewIndex(index);
        event?.currentTarget.blur();
      },
      serviceTypeCategory,
      setServiceTypeCategory,
    }),
    [isModalForAutomaticRecord, selectedReviewIndex, serviceTypeCategory]
  );

  return (
    <ReviewsModalContext.Provider value={contextValue}>{children}</ReviewsModalContext.Provider>
  );
}

export default ReviewsModalContext;
